import { useCallback, useEffect, useState } from "react";
import {
  getPortfolios, getSummary, createPortfolio, deletePortfolio,
  executeTrade, deleteTrade,
} from "./PaperTradingService";
import { getLatestPrice } from "./StockPriceService";

const fmtINR = (n) =>
  n == null ? "—" : new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(n);

const pnlClass = (n) => (n == null ? "" : n >= 0 ? "stocks-good" : "stocks-bad");

export default function PaperTrading() {
  const [portfolios,       setPortfolios]       = useState([]);
  const [selected,         setSelected]         = useState(null); // portfolioId
  const [summary,          setSummary]          = useState(null);
  const [loading,          setLoading]          = useState(true);
  const [summaryLoading,   setSummaryLoading]   = useState(false);
  const [showCreate,       setShowCreate]       = useState(false);
  const [showTrade,        setShowTrade]        = useState(false);
  const [tradeSymbol,      setTradeSymbol]      = useState("");
  const [tradeType,        setTradeType]        = useState("BUY");
  const [tradeQty,         setTradeQty]         = useState("");
  const [tradePrice,       setTradePrice]       = useState("");
  const [tradeTag,         setTradeTag]         = useState("");
  const [tradeNotes,       setTradeNotes]       = useState("");
  const [tradeErr,         setTradeErr]         = useState("");
  const [priceLoading,     setPriceLoading]     = useState(false);
  const [createName,       setCreateName]       = useState("");
  const [createDesc,       setCreateDesc]       = useState("");
  const [createCapital,    setCreateCapital]    = useState("100000");
  const [activeView,       setActiveView]       = useState("positions"); // positions | trades

  const loadPortfolios = useCallback(async () => {
    setLoading(true);
    try {
      const list = await getPortfolios();
      setPortfolios(list || []);
      if (!selected && list?.length > 0) setSelected(list[0].id);
    } finally { setLoading(false); }
  }, [selected]);

  useEffect(() => { loadPortfolios(); }, [loadPortfolios]);

  const loadSummary = useCallback(async (pid) => {
    if (!pid) return;
    setSummaryLoading(true);
    try { setSummary(await getSummary(pid)); }
    finally { setSummaryLoading(false); }
  }, []);

  useEffect(() => { loadSummary(selected); }, [selected, loadSummary]);

  const handleCreate = async () => {
    if (!createName.trim()) return;
    await createPortfolio({
      name: createName, description: createDesc,
      startingCapital: parseFloat(createCapital) || 100000,
    });
    setShowCreate(false); setCreateName(""); setCreateDesc(""); setCreateCapital("100000");
    await loadPortfolios();
  };

  const handleDelete = async (pid) => {
    if (!window.confirm("Delete this portfolio and all its trades?")) return;
    await deletePortfolio(pid);
    if (selected === pid) setSelected(null);
    await loadPortfolios();
  };

  const handleFetchPrice = async () => {
    if (!tradeSymbol.trim()) return;
    setPriceLoading(true);
    try {
      const data = await getLatestPrice(tradeSymbol.trim().toUpperCase());
      if (data?.price) setTradePrice(String(data.price));
    } catch { } finally { setPriceLoading(false); }
  };

  const handleTrade = async () => {
    setTradeErr("");
    if (!tradeSymbol || !tradeQty || !tradePrice) { setTradeErr("Symbol, quantity and price are required."); return; }
    try {
      await executeTrade(selected, {
        symbol: tradeSymbol.toUpperCase(), tradeType,
        quantity: parseInt(tradeQty), price: parseFloat(tradePrice),
        strategyTag: tradeTag || null, notes: tradeNotes || null,
      });
      setShowTrade(false);
      setTradeSymbol(""); setTradeQty(""); setTradePrice(""); setTradeTag(""); setTradeNotes("");
      await loadSummary(selected);
    } catch (e) { setTradeErr(e.message || "Trade failed"); }
  };

  const handleDeleteTrade = async (tradeId) => {
    if (!window.confirm("Undo this trade?")) return;
    await deleteTrade(selected, tradeId);
    await loadSummary(selected);
  };

  const portfolio = summary?.portfolio;
  const totalValue = portfolio
    ? (summary.currentValue ?? 0) + (portfolio.currentCash ?? 0)
    : null;

  return (
    <div className="stocks-section">
      {/* Portfolio list header */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <span className="card-title" style={{ flex: 1, margin: 0 }}>📝 Paper Portfolios</span>
        <button className="btn btn-sm" onClick={() => setShowCreate(true)}>+ New Portfolio</button>
      </div>

      {loading && <div>Loading…</div>}

      {/* Portfolio tabs */}
      {portfolios.length > 0 && (
        <div className="stocks-tabs" style={{ marginBottom: 16 }}>
          {portfolios.map(p => (
            <button key={p.id} className={`tab-btn ${selected === p.id ? "active" : ""}`}
              onClick={() => setSelected(p.id)}>
              {p.name}
            </button>
          ))}
        </div>
      )}

      {portfolios.length === 0 && !loading && (
        <div className="card" style={{ textAlign: "center", color: "var(--color-muted)", padding: 32 }}>
          No paper portfolios yet. Create one to start practising.
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-title">New Paper Portfolio</div>
            <label className="form-label">Portfolio name *</label>
            <input className="form-input" value={createName} onChange={e => setCreateName(e.target.value)} placeholder="e.g. RSI Strategy Test" />
            <label className="form-label">Description</label>
            <input className="form-input" value={createDesc} onChange={e => setCreateDesc(e.target.value)} placeholder="What am I testing?" />
            <label className="form-label">Starting capital (₹)</label>
            <input className="form-input" type="number" value={createCapital} onChange={e => setCreateCapital(e.target.value)} />
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button className="btn" onClick={handleCreate}>Create</button>
              <button className="btn btn-ghost" onClick={() => setShowCreate(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Portfolio detail */}
      {selected && summaryLoading && <div>Loading portfolio…</div>}
      {selected && summary && !summaryLoading && (
        <>
          {/* KPI strip */}
          <div className="stocks-kpis" style={{ marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
            <span className="stocks-kpi">
              <span className="stocks-kpi-label">Cash</span>
              <span className="stocks-kpi-value">{fmtINR(portfolio?.currentCash)}</span>
            </span>
            <span className="stocks-kpi">
              <span className="stocks-kpi-label">Invested</span>
              <span className="stocks-kpi-value">{fmtINR(summary.investedValue)}</span>
            </span>
            <span className="stocks-kpi">
              <span className="stocks-kpi-label">Total value</span>
              <span className="stocks-kpi-value">{fmtINR(totalValue)}</span>
            </span>
            <span className={`stocks-kpi ${pnlClass(summary.unrealizedPnl)}`}>
              <span className="stocks-kpi-label">Unrealised P&L</span>
              <span className="stocks-kpi-value">{fmtINR(summary.unrealizedPnl)}</span>
            </span>
            <span className={`stocks-kpi ${pnlClass(summary.realizedPnl)}`}>
              <span className="stocks-kpi-label">Realised P&L</span>
              <span className="stocks-kpi-value">{fmtINR(summary.realizedPnl)}</span>
            </span>
            <span className={`stocks-kpi ${pnlClass(summary.totalPnl)}`}>
              <span className="stocks-kpi-label">Total P&L</span>
              <span className="stocks-kpi-value">
                {fmtINR(summary.totalPnl)}
                {summary.totalPnlPct != null && (
                  <small style={{ marginLeft: 4 }}>({Number(summary.totalPnlPct).toFixed(2)}%)</small>
                )}
              </span>
            </span>
          </div>

          {/* Action row */}
          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            <button className="btn" onClick={() => { setTradeType("BUY"); setShowTrade(true); }}>🟢 Buy</button>
            <button className="btn btn-ghost" onClick={() => { setTradeType("SELL"); setShowTrade(true); }}>🔴 Sell</button>
            <div style={{ flex: 1 }} />
            <button className="btn btn-ghost btn-sm" onClick={() => handleDelete(selected)}>Delete portfolio</button>
          </div>

          {/* Sub-tabs */}
          <div className="stocks-tabs" style={{ marginBottom: 12 }}>
            {["positions", "trades"].map(v => (
              <button key={v} className={`tab-btn btn-sm ${activeView === v ? "active" : ""}`}
                onClick={() => setActiveView(v)}>
                {v === "positions" ? `Positions (${summary.positions?.length ?? 0})` : `All trades (${summary.trades?.length ?? 0})`}
              </button>
            ))}
          </div>

          {/* Positions */}
          {activeView === "positions" && (
            summary.positions?.length === 0 ? (
              <div style={{ color: "var(--color-muted)", padding: 12 }}>No open positions. Use Buy to enter a trade.</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="table-base">
                  <thead><tr>
                    <th>Symbol</th><th>Qty</th><th>Avg cost</th>
                    <th>Cur price</th><th>Value</th><th>P&L</th><th>%</th>
                  </tr></thead>
                  <tbody>
                    {summary.positions?.map(p => (
                      <tr key={p.symbol}>
                        <td><strong>{p.symbol}</strong>{p.stockName && <><br /><small style={{ color: "var(--color-muted)" }}>{p.stockName}</small></>}</td>
                        <td>{p.qty}</td>
                        <td>{fmtINR(p.avgCost)}</td>
                        <td>{p.currentPrice ? fmtINR(p.currentPrice) : <span style={{ color: "var(--color-muted)" }}>—</span>}</td>
                        <td>{fmtINR(p.currentValue)}</td>
                        <td className={pnlClass(p.unrealizedPnl)}>{fmtINR(p.unrealizedPnl)}</td>
                        <td className={pnlClass(p.unrealizedPnlPct)}>
                          {p.unrealizedPnlPct != null ? `${Number(p.unrealizedPnlPct).toFixed(2)}%` : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* Trade history */}
          {activeView === "trades" && (
            summary.trades?.length === 0 ? (
              <div style={{ color: "var(--color-muted)", padding: 12 }}>No trades yet.</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table className="table-base">
                  <thead><tr>
                    <th>Date</th><th>Symbol</th><th>Type</th><th>Qty</th><th>Price</th><th>Value</th><th>Strategy</th><th>Notes</th><th></th>
                  </tr></thead>
                  <tbody>
                    {summary.trades?.map(t => (
                      <tr key={t.id}>
                        <td style={{ whiteSpace: "nowrap" }}>{t.tradeDate}</td>
                        <td><strong>{t.symbol}</strong></td>
                        <td><span className={t.tradeType === "BUY" ? "stocks-good" : "stocks-bad"}>{t.tradeType}</span></td>
                        <td>{t.quantity}</td>
                        <td>{fmtINR(t.price)}</td>
                        <td>{fmtINR((t.price ?? 0) * (t.quantity ?? 0))}</td>
                        <td><small>{t.strategyTag || "—"}</small></td>
                        <td><small>{t.notes || ""}</small></td>
                        <td>
                          <button className="btn btn-ghost btn-sm" style={{ color: "var(--color-danger)" }}
                            onClick={() => handleDeleteTrade(t.id)}>Undo</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </>
      )}

      {/* Trade modal */}
      {showTrade && (
        <div className="modal-overlay" onClick={() => setShowTrade(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-title">{tradeType === "BUY" ? "🟢 Paper Buy" : "🔴 Paper Sell"}</div>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <button className={`btn btn-sm ${tradeType === "BUY" ? "" : "btn-ghost"}`} onClick={() => setTradeType("BUY")}>Buy</button>
              <button className={`btn btn-sm ${tradeType === "SELL" ? "" : "btn-ghost"}`} onClick={() => setTradeType("SELL")}>Sell</button>
            </div>
            <label className="form-label">Symbol *</label>
            <div style={{ display: "flex", gap: 6 }}>
              <input className="form-input" style={{ flex: 1 }} value={tradeSymbol}
                onChange={e => setTradeSymbol(e.target.value.toUpperCase())} placeholder="e.g. RELIANCE" />
              <button className="btn btn-ghost btn-sm" onClick={handleFetchPrice} disabled={priceLoading}>
                {priceLoading ? "…" : "Fetch price"}
              </button>
            </div>
            <label className="form-label">Price (₹) *</label>
            <input className="form-input" type="number" value={tradePrice} onChange={e => setTradePrice(e.target.value)} placeholder="Enter or fetch current price" />
            <label className="form-label">Quantity *</label>
            <input className="form-input" type="number" value={tradeQty} onChange={e => setTradeQty(e.target.value)} />
            {tradePrice && tradeQty && (
              <div style={{ color: "var(--color-muted)", fontSize: 12, marginBottom: 6 }}>
                Total: {fmtINR(parseFloat(tradePrice) * parseInt(tradeQty))}
              </div>
            )}
            <label className="form-label">Strategy tag (optional)</label>
            <input className="form-input" value={tradeTag} onChange={e => setTradeTag(e.target.value)} placeholder="e.g. RSI Mean Reversion" />
            <label className="form-label">Notes (optional)</label>
            <input className="form-input" value={tradeNotes} onChange={e => setTradeNotes(e.target.value)} placeholder="Why am I entering this trade?" />
            {tradeErr && <div style={{ color: "var(--color-danger)", margin: "6px 0" }}>{tradeErr}</div>}
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button className="btn" onClick={handleTrade}>
                {tradeType === "BUY" ? "Execute Buy" : "Execute Sell"}
              </button>
              <button className="btn btn-ghost" onClick={() => { setShowTrade(false); setTradeErr(""); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
